"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { MapPin, Globe, Users, Compass, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { motion } from "framer-motion";
export default function Footprint() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  const stats = [{
    value: "5",
    label: "YEARS OF EXPLORATION",
    icon: Compass,
    description: "A decade of crafting bespoke travel experiences."
  }, {
    value: "45",
    label: "GLOBAL DESTINATIONS",
    icon: Globe,
    description: "From the peaks of Switzerland to the shores of Bali."
  }, {
    value: "120",
    label: "CURATED PARTNERS",
    icon: Users,
    description: "Exclusive access to the world's finest retreats."
  }, {
    value: "100+",
    label: "MEMORABLE JOURNEYS",
    icon: MapPin,
    description: "Thousands of stories written across the horizon."
  }];
  return <section data-section-id="13556" id="global-footprint" className="relative overflow-hidden bg-background py-12 lg:py-18 px-6 md:px-0">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 -skew-x-12 translate-x-1/2 pointer-events-none" />

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-9 items-end mb-12 md:mb-16">
          <div className="lg:col-span-7 lg:col-start-1">
            <span className="inline-block font-sans text-[0.75rem] font-bold tracking-[0.2em] text-primary uppercase mb-6">
              Our Global Footprint
            </span>
            <h2 className="font-serif font-light text-foreground leading-[1.1] text-4xl md:text-6xl lg:text-7xl">
              Connecting travelers to <br />
              <em className="italic font-medium text-primary">extraordinary</em> horizons
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 pb-2">
            <p className="font-serif text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              We don't just book trips; we architect legacies of adventure. Our reach spans continents, bringing the world's most secluded wonders to your doorstep.
            </p>
            <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-8 py-6 h-auto text-lg group">
              <Link to="https://innoleafstays.com/international-escapes/">
                Explore Destinations
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, i) => <motion.div key={i} data-index={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="group relative p-8 bg-card border border-border rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="mb-8 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                <stat.icon strokeWidth={1.5} className="w-6 h-6" />
              </div>
              
              <div className="space-y-2">
                <div className="font-serif text-5xl md:text-6xl font-light text-foreground tracking-tighter">
                  {stat.value}
                </div>
                <div className="font-sans text-[0.7rem] font-bold tracking-[0.15em] text-primary uppercase">
                  {stat.label}
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-border">
                <p className="font-serif text-muted-foreground leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </motion.div>)}
        </div>

        {}
        <div className="mt-16 md:mt-24 text-center">
          <div className="inline-flex items-center gap-4 text-muted-foreground/30">
            <div className="h-px w-12 bg-current" />
            <span className="font-serif italic text-xl">Est. 2012 — Innoleaf Journeys</span>
            <div className="h-px w-12 bg-current" />
          </div>
        </div>
      </div>
    </section>;
}